/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the software.

*/

import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import { CircularProgress } from "@mui/material";

import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";

import VuiBox from "components/VuiBox";

import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import { GuestProfileProvider } from "components/GuestProfileDrawer";

import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

import routes from "routes";

import { useVisionUIController, setMiniSidenav, setOpenConfigurator } from "context";

import useCommon from "hooks/useCommon";

import { restoreSession } from "store/slice/userSlice";

import RequireAuth from "layouts/authentication/require-auth/RequireAuth";
import SignIn from "layouts/authentication/sign-in";
import Unauthorized from "layouts/authentication/unauthorized";
import SurveyBot from "layouts/bot/SurveyBot";
import VerifyEmail from "layouts/authentication/email-verification";
import RolePermissionsUpdater from "layouts/dev/RolePermissionsUpdater";
import ResetPassword from "layouts/authentication/reset-password";
import NotFound from "layouts/errors/NotFound";
import GuestSatisfactionFormSubmission from "layouts/forms/GuestSatisfactionForm";

import { subscribeToHousekeepingPusher } from "services/housekeepingPusher";
import { getHousekeepingNotifications } from "store/slice/pusherSlice";

export default function App() {
  const [controller, dispatch] = useVisionUIController();

  const { DISPATCH, STORE } = useCommon();

  const { authInitialized, user } = STORE((state) => state.user);

  const hotelIds = STORE((state) => state.ui.hotelIds);

  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);

  const [isPageChanging, setIsPageChanging] = useState(false);

  const [rtlCache, setRtlCache] = useState(null);

  const { pathname } = useLocation();

  const currentUser = user?.user || user;

  const userId = currentUser?._id || currentUser?.id;

  const hotelId = hotelIds?.[0];

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // =========================================
  // SIDENAV
  // =========================================

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);

      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);

      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // =========================================
  // DOCUMENT DIRECTION
  // =========================================

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // =========================================
  // PAGE CHANGE
  // =========================================

  useEffect(() => {
    setIsPageChanging(true);

    const t = setTimeout(() => {
      document.documentElement.scrollTop = 0;

      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
      }

      setIsPageChanging(false);
    }, 120);

    return () => clearTimeout(t);
  }, [pathname]);

  // =========================================
  // ROUTE TYPES
  // =========================================

  const emptyRoute = pathname === "/";

  const devRoute = pathname.startsWith("/dev");

  const isAuthRoute = pathname.startsWith("/authentication") || pathname === "/verify-email";

  const isWelcomeRoute = pathname === "/welcome";

  const isGuestSurveyRoute = pathname === "/welcome/guest-survey";

  const isPublicRoute = isWelcomeRoute || isGuestSurveyRoute;

  const showTheme = !isWelcomeRoute;

  const showSidenav =
    !isAuthRoute && !isPublicRoute && !emptyRoute && !devRoute && layout === "dashboard";

  // =========================================
  // GLOBAL PUSHER SUBSCRIPTION
  // =========================================

  useEffect(() => {
    if (!authInitialized || !userId || !hotelId || isAuthRoute || isPublicRoute) {
      return;
    }

    // console.log("Initializing housekeeping Pusher:", {
    //   userId,
    //   hotelId,
    // });

    const unsubscribe = subscribeToHousekeepingPusher({
      hotelId,
      userId,
      dispatch: DISPATCH,
    });

    return () => {
      console.log("Cleaning up housekeeping Pusher:", {
        userId,
        hotelId,
      });

      unsubscribe?.();
    };
  }, [authInitialized, userId, hotelId, isAuthRoute, isPublicRoute, DISPATCH]);

  // =========================================
  // ROUTES
  // =========================================

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        const element = route.protected ? (
          <RequireAuth route={route} permission={route.permission}>
            {route.component}
          </RequireAuth>
        ) : (
          route.component
        );

        return <Route path={route.route} element={element} key={route.key} />;
      }

      return null;
    });

  // =========================================
  // RESTORE USER SESSION
  // =========================================

  useEffect(() => {
    if (pathname === "/welcome") {
      return;
    }

    DISPATCH(restoreSession());
  }, [DISPATCH]);

  // =========================================
  // GET HOUSEKEEPING NOTIFICATIONS
  // =========================================

  useEffect(() => {
    if (!authInitialized || !userId || isAuthRoute || isPublicRoute) {
      return;
    }

    DISPATCH(
      getHousekeepingNotifications({
        page: 1,
        pageSize: 20,
        unreadOnly: false,
      })
    );
  }, [authInitialized, userId, isAuthRoute, isPublicRoute, DISPATCH]);

  // =========================================
  // CONFIG BUTTON
  // =========================================

  const configsButton = (
    <VuiBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgcolor="primary"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      sx={{
        cursor: "pointer",
      }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="default" color="inherit">
        settings
      </Icon>
    </VuiBox>
  );

  // =========================================
  // AUTH LOADER
  // =========================================

  if (!authInitialized && !isWelcomeRoute) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <VuiBox display="flex" justifyContent="center" alignItems="center" height="var(--app-viewport-height, 100vh)">
          <CircularProgress size={40} color="primary" />
        </VuiBox>
      </ThemeProvider>
    );
  }

  // =========================================
  // APP CONTENT
  // =========================================

  const AppContent = (
    <GuestProfileProvider>
      {isWelcomeRoute ? (
        <Routes>
          <Route path="/welcome" element={<SurveyBot />} />
        </Routes>
      ) : (
        <>
          {showSidenav && (
            <>
              <Sidenav
                color={sidenavColor}
                brand=""
                brandName="SARTO"
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />

              <Configurator />
            </>
          )}

          {layout === "vr" && <Configurator />}

          <VuiBox
            sx={{
              transition: "opacity 120ms ease-out, transform 120ms ease-out",
              willChange: "opacity, transform",
            }}
          >
            <Routes>
              {getRoutes(routes)}

              <Route path="/authentication/sign-in" element={<SignIn />} />

              <Route path="/verify-email" element={<VerifyEmail />} />

              <Route path="/authentication/reset-password" element={<ResetPassword />} />

              <Route path="/welcome" element={<SurveyBot />} />

              <Route path="/welcome/guest-survey" element={<GuestSatisfactionFormSubmission />} />

              <Route path="/unauthorized" element={<Unauthorized />} />

              <Route path="/" element={<Navigate to="/authentication/sign-in" replace />} />

              <Route path="/dev/role-update" element={<RolePermissionsUpdater />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </VuiBox>
        </>
      )}
    </GuestProfileProvider>
  );

  // =========================================
  // RENDER
  // =========================================

  return showTheme ? (
    <ThemeProvider theme={theme} key={pathname}>
      <CssBaseline />

      {AppContent}
    </ThemeProvider>
  ) : (
    AppContent
  );
}
